import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FiSunrise, FiSunset, FiArrowRight, FiSearch, FiCalendar } from 'react-icons/fi';
import { GiSun, GiMoon } from 'react-icons/gi';
import { useHydrated } from '../../hooks/useHydrated';
import { useTranslation } from '../../hooks/useTranslation';
import { tithiTamil, nakshatraTamil, yogaTamil, pakshaTamil, rashiTamil, tamilYogaTamil, translatePanchangValue } from '../../data/panchangTamil';
import './PanchangHero.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const todayStr = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const PanchangHero = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(todayStr());
  const [cityInput, setCityInput] = useState('Chennai');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const suggestionsRef = useRef(null);
  const hydrated = useHydrated();
  const { t, lang } = useTranslation();

  const tp = (value, mapping) => lang === 'ta' ? translatePanchangValue(value, mapping) : value;
  const isUserTyping = useRef(false);

  const fetchPanchang = async (cityName, locData, date) => {
    setLoading(true);
    try {
      const fetchDate = date || todayStr();
      let params = { date: fetchDate };

      if (locData) {
        params = {
          ...params,
          lat: locData.latitude,
          lon: locData.longitude,
          tz: locData.timezone,
          city: locData.city,
          state: locData.state,
          country: locData.country,
        };
      } else {
        params.location = cityName || 'Chennai';
      }

      const response = await axios.get(`${API_BASE_URL}/panchang/date`, { params });
      setData(response.data.data);

      const loc = response.data.data?.location;
      if (loc && !locData) {
        const name = loc.city === 'Custom' ? t('panchang.yourLocation') : loc.city;
        const display = [name, loc.state, loc.country].filter(Boolean).join(', ');
        setCityInput(display);
      }
    } catch (err) {
      console.error('Panchang fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load Chennai with today's date on mount
  useEffect(() => {
    fetchPanchang('Chennai', null, todayStr());
  }, []);

  // Close suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchCities = useCallback(async (query) => {
    if (query.length < 2) { setSuggestions([]); return; }
    try {
      const response = await axios.get(`${API_BASE_URL}/panchang/cities`, { params: { q: query } });
      setSuggestions(response.data.data || []);
      setShowSuggestions(true);
    } catch {
      setSuggestions([]);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (cityInput.length >= 2 && !selectedLocation && isUserTyping.current) {
        searchCities(cityInput);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [cityInput, searchCities, selectedLocation]);

  const handleCitySelect = (city) => {
    isUserTyping.current = false;
    const displayName = city.state
      ? `${city.city}, ${city.state}, ${city.country}`
      : `${city.city}, ${city.country}`;
    setCityInput(displayName);
    setSelectedLocation(city);
    setShowSuggestions(false);
    fetchPanchang(city.city, city, selectedDate);
  };

  const handleSearch = () => {
    setShowSuggestions(false);
    if (selectedLocation) {
      fetchPanchang(selectedLocation.city, selectedLocation, selectedDate);
    } else {
      fetchPanchang(cityInput, null, selectedDate);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    if (selectedLocation) {
      fetchPanchang(selectedLocation.city, selectedLocation, newDate);
    } else {
      fetchPanchang(cityInput, null, newDate);
    }
  };

  return (
    <section className="panchang-hero">
      <div className="hero-bg-elements">
        <div className="hero-planet planet-1"><GiSun /></div>
        <div className="hero-planet planet-2"><GiMoon /></div>
        <div className="hero-planet planet-3">&#9765;</div>
        <div className="hero-orbit orbit-1"></div>
        <div className="hero-orbit orbit-2"></div>
        <div className="hero-orbit orbit-3"></div>
      </div>

      <div className="container ph-container">
        {/* Left Content */}
        <motion.div
          className="ph-content"
          initial={hydrated ? { opacity: 0, x: -50 } : false}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
        >
          <span className="hero-badge">{t('panchang.dailyPanchangam')}</span>
          <h1>
            {t('panchang.todaysPanchang')}
          </h1>
          <p className="ph-desc">
            {t('panchang.panchangDesc')}
          </p>
          <div className="hero-stats">
            <div className="stat-item">
              <span className="stat-number">27</span>
              <span className="stat-label">{t('panchang.nakshatras')}</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">30</span>
              <span className="stat-label">{t('panchang.tithis')}</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">150+</span>
              <span className="stat-label">{t('panchang.cities')}</span>
            </div>
          </div>
        </motion.div>

        {/* Right Card */}
        <motion.div
          className="ph-card-wrapper"
          initial={hydrated ? { opacity: 0, x: 50 } : false}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="ph-main-card">
            <div className="ph-card-header">
              <h3>{t('panchang.todaysPanchang')}</h3>
              {data && <p className="ph-card-date">{data.date}</p>}
            </div>

            {/* Date Picker */}
            <div className="ph-date-row">
              <FiCalendar className="ph-date-icon" />
              <input
                type="date"
                value={selectedDate}
                onChange={handleDateChange}
                className="ph-date-input"
                max={new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0]}
              />
            </div>

            {/* City Search */}
            <div className="ph-city-search" ref={suggestionsRef}>
              <div className="ph-search-row">
                <input
                  type="text"
                  value={cityInput}
                  onChange={(e) => {
                    isUserTyping.current = true;
                    setCityInput(e.target.value);
                    setSelectedLocation(null);
                  }}
                  onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                  onKeyDown={handleKeyDown}
                  placeholder={t('panchang.searchCity')}
                  className="ph-search-input"
                />
                <button onClick={handleSearch} className="ph-search-btn">
                  <FiSearch />
                </button>
              </div>
              {showSuggestions && suggestions.length > 0 && (
                <ul className="ph-suggestions">
                  {suggestions.map((city, idx) => (
                    <li
                      key={`${city.city}-${city.country}-${idx}`}
                      onClick={() => handleCitySelect(city)}
                      className="ph-suggestion-item"
                    >
                      <span className="ph-sug-city">{city.city}</span>
                      <span className="ph-sug-detail">
                        {city.state ? `${city.state}, ` : ''}{city.country}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Loading */}
            {loading && (
              <div className="ph-loading">
                <div className="ph-spinner"></div>
              </div>
            )}

            {/* Panchang Data */}
            {data && !loading && (
              <div className="ph-data">
                {/* City */}
                <div className="ph-row">
                  <span className="ph-label">{t('panchang.city')}</span>
                  <span className="ph-val">
                    {data.location.city === 'Custom' ? t('panchang.yourLocation') : data.location.city}
                    {data.location.state ? `, ${data.location.state}` : ''}
                    {data.location.country ? `, ${data.location.country}` : ''}
                  </span>
                </div>

                {/* Sunrise / Sunset */}
                <div className="ph-sun-row">
                  <div className="ph-sun-item">
                    <FiSunrise className="ph-sun-icon sunrise" />
                    <div>
                      <span className="ph-mini-label">{t('panchang.sunrise')}</span>
                      <span className="ph-sun-time">{data.sunrise}</span>
                    </div>
                  </div>
                  <div className="ph-sun-item">
                    <FiSunset className="ph-sun-icon sunset" />
                    <div>
                      <span className="ph-mini-label">{t('panchang.sunset')}</span>
                      <span className="ph-sun-time">{data.sunset}</span>
                    </div>
                  </div>
                </div>

                <div className="ph-divider"></div>

                {/* Tithi */}
                <div className="ph-detail-row">
                  <span className="ph-label">{t('panchang.tithi')} ({tp(data.tithi.paksha, pakshaTamil)} {lang === 'ta' ? 'பக்ஷம்' : 'Paksham'})</span>
                  <span className="ph-val">{tp(data.tithi.name, tithiTamil)}</span>
                  <span className="ph-sub-info">{lang === 'ta' ? 'வரை' : 'till'} {data.tithi.endTime} &middot; {lang === 'ta' ? 'அடுத்தது' : 'Followed By'} {tp(data.tithi.followedBy, tithiTamil)}</span>
                </div>

                {/* Nakshatra */}
                <div className="ph-detail-row">
                  <span className="ph-label">{t('panchang.nakshatra')}</span>
                  <span className="ph-val">{tp(data.nakshatra.name, nakshatraTamil)}</span>
                  <span className="ph-sub-info">{lang === 'ta' ? 'வரை' : 'till'} {data.nakshatra.endTime} &middot; {lang === 'ta' ? 'அடுத்தது' : 'Followed By'} {tp(data.nakshatra.followedBy, nakshatraTamil)}</span>
                </div>

                {/* Yoga */}
                <div className="ph-detail-row">
                  <span className="ph-label">{t('panchang.yoga')}</span>
                  <span className="ph-val">{tp(data.yoga.name, yogaTamil)}</span>
                  <span className="ph-sub-info">{lang === 'ta' ? 'வரை' : 'till'} {data.yoga.endTime} &middot; {lang === 'ta' ? 'அடுத்தது' : 'Followed By'} {tp(data.yoga.followedBy, yogaTamil)}</span>
                </div>

                {/* Tamil Yoga */}
                <div className="ph-detail-row">
                  <span className="ph-label">{t('panchang.tamilYoga')}</span>
                  <span className="ph-val">{tp(data.tamilYoga.name, tamilYogaTamil)} - {tp(data.yoga.name, yogaTamil)}</span>
                </div>

                <div className="ph-divider"></div>

                {/* Inauspicious Times */}
                <div className="ph-row ph-row-warn">
                  <span className="ph-label">{t('panchang.rahuKalam')}</span>
                  <span className="ph-val">{data.rahuKalam.start} - {data.rahuKalam.end}</span>
                </div>
                <div className="ph-row ph-row-warn">
                  <span className="ph-label">{t('panchang.gulikaiKalam')}</span>
                  <span className="ph-val">{data.gulikaKalam.start} - {data.gulikaKalam.end}</span>
                </div>
                <div className="ph-row ph-row-warn">
                  <span className="ph-label">{t('panchang.yamaganda')}</span>
                  <span className="ph-val">{data.yamaganda.start} - {data.yamaganda.end}</span>
                </div>

                <div className="ph-divider"></div>

                {/* Auspicious */}
                <div className="ph-row ph-row-good">
                  <span className="ph-label">{t('panchang.abhijitMuhurta')}</span>
                  <span className="ph-val">{data.abhijitMuhurta.start} - {data.abhijitMuhurta.end}</span>
                </div>

                <div className="ph-divider"></div>

                {/* Chandrashtama */}
                <div className="ph-detail-row">
                  <span className="ph-label">{t('panchang.chandrashtama')}</span>
                  <span className="ph-val">{tp(data.chandrashtama.affectedRashi, rashiTamil)} {lang === 'ta' ? 'ராசி' : 'Rasi'}</span>
                  <span className="ph-sub-info">{data.chandrashtama.nakshatrasInAffectedRashi}</span>
                </div>
              </div>
            )}

            <Link to="/panchangam" className="btn btn-primary btn-full ph-view-btn">
              {t('panchang.viewFullPanchangam')} <FiArrowRight />
            </Link>
          </div>
        </motion.div>
      </div>

      <div className="hero-scroll-indicator">
        <span>{t('panchang.scrollToExplore')}</span>
        <div className="scroll-arrow"></div>
      </div>
    </section>
  );
};

export default PanchangHero;
